/**
 * ManualTimeEditor (GDN-2510)
 *
 * The "Set manually" editor for the official time selector. Manual entry is
 * date + start + DURATION (not start/end) — providers think in visit lengths.
 * Mirrors the session popover's duration row (10/20/30/60/pencil-custom
 * ButtonGroup + computed "Ends at") and the calendar TimeEditor's date+time
 * draft row.
 *
 * Two flavors:
 *   - showDateField (recording screen): date + start + duration; the
 *     resolution strip reads "On the note & claim: {date} · {start} – {end}".
 *   - no date (note popover): start + duration only — the Date of Service
 *     field above the selector owns the billing date; the strip reads
 *     "Session time on the note: {start} – {end}".
 *
 * Commit discipline: `onCommit` fires only when the value is COMPLETE
 * (date + start + duration) — on blur of the text fields and on duration
 * picks. Incomplete values never write.
 */

import React, { useState } from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  ButtonGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import {
  computeEndClock,
  DURATION_PRESETS,
  formatClock,
  formatDateLong,
  isManualComplete,
  type ManualTimeValue,
} from './officialTimeModel';

export interface ManualTimeEditorProps {
  value: ManualTimeValue;
  onChange: (next: ManualTimeValue) => void;
  /** Fired with a complete value at commit points (blur / duration pick). */
  onCommit: (value: ManualTimeValue) => void;
  /** Recording screen shows the date; the note popover's DOS field owns it. */
  showDateField: boolean;
  disabled?: boolean;
}

/** "On the note & claim: May 19, 2026 · 1:30 PM – 2:00 PM" (with date). */
const resolveWithDate = (manual: ManualTimeValue): string => {
  const datePart = formatDateLong(manual.date);
  if (!manual.start) return `${datePart} · time not set`;
  const end = computeEndClock(manual.start, manual.durationMin);
  if (!end) return `${datePart} · ${formatClock(manual.start)} · duration not set`;
  return `${datePart} · ${formatClock(manual.start)} – ${end}`;
};

/** "1:30 – 2:00 PM" (time-of-day only — the DOS field owns the date). */
const resolveTimeOnly = (manual: ManualTimeValue): string => {
  if (!manual.start) return 'time not set';
  const end = computeEndClock(manual.start, manual.durationMin);
  if (!end) return `${formatClock(manual.start)} · duration not set`;
  return `${formatClock(manual.start).replace(/ (AM|PM)$/, '')} – ${end}`;
};

export const ManualTimeEditor: React.FC<ManualTimeEditorProps> = ({
  value,
  onChange,
  onCommit,
  showDateField,
  disabled = false,
}) => {
  const theme = useTheme();
  const isPreset = value.durationMin !== null && DURATION_PRESETS.includes(value.durationMin);
  const [isCustomMode, setIsCustomMode] = useState(!isPreset && value.durationMin !== null);
  const [customDuration, setCustomDuration] = useState(
    !isPreset && value.durationMin !== null ? String(value.durationMin) : '',
  );

  const endClock = computeEndClock(value.start, value.durationMin);

  const commitIfComplete = (next: ManualTimeValue) => {
    if (isManualComplete(next)) onCommit(next);
  };

  return (
    <Box
      data-testid="manual-time-editor"
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Date + start (recording screen) or start only (note popover) */}
      {showDateField ? (
        <Stack direction="row" spacing={1}>
          <TextField
            type="date"
            label="Date"
            size="small"
            fullWidth
            disabled={disabled}
            value={value.date}
            onChange={e => onChange({ ...value, date: e.target.value })}
            onBlur={() => commitIfComplete(value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: 1.3 }}
          />
          <TextField
            type="time"
            label="Start time"
            size="small"
            fullWidth
            disabled={disabled}
            value={value.start}
            onChange={e => onChange({ ...value, start: e.target.value })}
            onBlur={() => commitIfComplete(value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ flex: 1 }}
          />
        </Stack>
      ) : (
        <TextField
          type="time"
          label="Start time"
          size="small"
          fullWidth
          disabled={disabled}
          value={value.start}
          onChange={e => onChange({ ...value, start: e.target.value })}
          onBlur={() => commitIfComplete(value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}

      {/* Duration — mirrors SessionDetailsSection's preset ButtonGroup */}
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Duration (minutes)
        </Typography>
        <ButtonGroup size="small" fullWidth disabled={disabled} sx={{ mb: isCustomMode ? 1 : 0 }}>
          {DURATION_PRESETS.map(preset => (
            <Button
              key={preset}
              variant={value.durationMin === preset && !isCustomMode ? 'contained' : 'outlined'}
              onClick={() => {
                const next = { ...value, durationMin: preset };
                onChange(next);
                setCustomDuration('');
                setIsCustomMode(false);
                commitIfComplete(next);
              }}
            >
              {preset}
            </Button>
          ))}
          <Tooltip title="Custom duration">
            <Button
              variant={isCustomMode ? 'contained' : 'outlined'}
              onClick={() => setIsCustomMode(true)}
              sx={{ minWidth: 36, px: 0 }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </ButtonGroup>
        {isCustomMode && (
          <TextField
            type="number"
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="Enter minutes"
            value={customDuration}
            onChange={e => {
              setCustomDuration(e.target.value);
              const val = parseInt(e.target.value, 10);
              if (val > 0) onChange({ ...value, durationMin: val });
            }}
            onBlur={() => commitIfComplete(value)}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        )}
        {/* Computed end time — read-only, like the session popover */}
        {endClock && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
            Ends at {endClock}
          </Typography>
        )}
      </Box>

      {/* Resolution strip — exactly what stamps onto the note & claim */}
      <Box
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 0.75,
          borderRadius: 1.5,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
          {showDateField ? 'On the note & claim:' : 'Session time on the note:'}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {showDateField ? resolveWithDate(value) : resolveTimeOnly(value)}
        </Typography>
      </Box>
    </Box>
  );
};
